import { fabric } from 'fabric';
import { ANNOTATION_TYPE, BCC_LABELS, DEFAULT_FONT } from '@/shared/ui';
import { getBpm, getSeconds, bpmToSec } from '@/shared/ui/components/FabricJS/Helper';

type BeatComplexCalipersCtor = new (
  cv: fabric.Canvas,
  bccObj: Record<string, number>,
  offsetX: number,
  config: any,
  verticalChannelPlacement: number[],
  channelIndex: number,
) => any;

let BeatComplexCalipersImpl: any;

(function () {
  const STROKE = {
    strokeWidth: 2,
    selectable: false,
    hasBorders: false,
    stroke: 'indigo',
    originX: 'center',
    originY: 'center',
    hoverCursor: 'arrow',
  };

  BeatComplexCalipersImpl = function (
    cv: fabric.Canvas,
    bccObj: Record<string, number>,
    offsetX: number,
    config: any,
    verticalChannelPlacement: number[],
    channelIndex: number,
  ) {
    this.type = ANNOTATION_TYPE.BCC;
    this.canvas = cv;
    this.channelIndex = channelIndex;
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.isVisible = true;
    this.l = [];
    this.labels = [];
    this.x = [];
    this.xr = [];
    this.backgroundColor = 'rgb(113, 138, 250)';

    for (let i = 0; i < BCC_LABELS.length; i += 1) {
      this.xr.push(bccObj[BCC_LABELS[i]]);
    }

    this.xr.forEach((element: number) => {
      this.x.push(Math.round((element - offsetX) * config.indexToPix));
    });

    this.selectedIndex = null;
    this.spp = (config.samplingRate * config.wndDuration) / config.width;
    this.stringValue = ` Rate: ${0} PR: ${0} QRS: ${0} QT: ${0} QTc: ${0} `;
  };

  BeatComplexCalipersImpl.prototype.draw = function () {
    this.x.forEach((val: number, index: number) => {
      const line = new fabric.Line([val, 0, val, 0], STROKE);
      this.l.push(line);
      const label = new fabric.Text(BCC_LABELS[index], {
        left: val,
        top: 0,
        selectable: false,
        fontSize: 14,
        fontFamily: DEFAULT_FONT,
        textAlign: 'center',
        fill: 'indigo',
        hoverCursor: 'arrow',
      });
      this.labels.push(label);
      this.canvas.add(line, label);
    });
    this.text = new fabric.Text(this.stringValue, {
      left: this.x[2],
      top: 0,
      selectable: false,
      fontFamily: DEFAULT_FONT,
      fontSize: 14,
      textAlign: 'center',
      backgroundColor: this.backgroundColor,
      fill: 'white',
      visible: false,
      hoverCursor: 'arrow',
    });
    this.canvas.add(this.text);

    // *: Set text value
    this.updateTextData();
  };

  BeatComplexCalipersImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  BeatComplexCalipersImpl.prototype.update = function (delta: { dx: number }) {
    if (this.selectedIndex !== null) {
      this.l[this.selectedIndex].set({
        x1: this.l[this.selectedIndex].x1 + delta.dx,
        x2: this.l[this.selectedIndex].x2 + delta.dx,
      });
      this.l[this.selectedIndex].setCoords();

      this.labels[this.selectedIndex].set({
        left: this.labels[this.selectedIndex].left + delta.dx,
      });
      this.labels[this.selectedIndex].setCoords();

      // *: If R-line update, update text position
      if (this.selectedIndex === 2) {
        this.adjustTextPos();
        this.text.setCoords();
      }

      this.x[this.selectedIndex] += delta.dx;
      this.xr[this.selectedIndex] += delta.dx * this.spp;

      // *: Update text value
      this.updateTextData();
    }
  };

  BeatComplexCalipersImpl.prototype.updateConfig = function (
    config: any,
    offsetX: number,
    verticalChannelPlacement: number[],
    displayedChannelsArr: number[],
  ) {
    this.x = [];
    this.xr.forEach((element: number) => {
      this.x.push(Math.round((element - offsetX) * config.indexToPix));
    });
    this.config = config;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.spp = (config.samplingRate * config.wndDuration) / config.width;
    for (let j = 0; j < displayedChannelsArr.length; j += 1) {
      if (this.channelIndex === displayedChannelsArr[j]) {
        const yPos = this.verticalChannelPlacement[j];
        const halfHeight = this.config.height / 3 / displayedChannelsArr.length;

        this.isVisibility(true);
        for (let i = 0; i < this.x.length; i += 1) {
          this.x[i] = (this.xr[i] - offsetX) / this.spp;
          this.l[i].set({
            x1: this.x[i],
            x2: this.x[i],
            y1: yPos - halfHeight,
            y2: yPos + halfHeight,
          });
          this.l[i].setCoords();
          this.labels[i].set({
            top: yPos - halfHeight - 20,
            fontSize: 14,
          });
          this.labels[i].set({ left: this.x[i] - this.labels[i].width / 2 });
          this.labels[i].setCoords();

          // *: Set text position
          if (i === 2) {
            this.text.set({
              top: yPos - halfHeight * 0.75,
              fontSize: 14,
            });
            this.adjustTextPos();
            this.text.setCoords();
          }

          // *: Make them visible
          if (this.x[i] < 0 || this.x[i] > this.config.width) {
            this.l[i].visible = false;
            this.labels[i].visible = false;
          }
        }

        this.checkTextCondition();
        break;
      } else {
        // *: Hide if zoom to another channel
        this.isVisibility(false);
      }
    }
  };

  BeatComplexCalipersImpl.prototype.scroll = function (
    offsetX: number,
    displayedChannelsArr: number[],
  ) {
    for (let j = 0; j < displayedChannelsArr.length; j += 1) {
      if (this.channelIndex === displayedChannelsArr[j]) {
        for (let i = 0; i < this.x.length; i += 1) {
          this.x[i] = (this.xr[i] - offsetX) / this.spp;
          this.l[i].set({
            x1: this.x[i],
            x2: this.x[i],
          });
          this.l[i].setCoords();
          this.labels[i].set({ left: this.x[i] - this.labels[i].width / 2 });
          this.labels[i].setCoords();

          // *: Set text position
          if (i === 2) {
            this.adjustTextPos();
            this.text.setCoords();
          }

          if (this.x[i] < 0 || this.x[i] > this.config.width) {
            this.l[i].visible = false;
            this.labels[i].visible = false;
          } else {
            this.l[i].visible = true;
            this.labels[i].visible = true;
          }
        }
        break;
      }
    }
    this.checkTextCondition();
  };

  BeatComplexCalipersImpl.prototype.isSelected = function (pointer: { x: number; y: number }) {
    for (let i = 0; i < this.l.length; i += 1) {
      if (
        pointer.y > this.l[i].y1 &&
        pointer.y < this.l[i].y2 &&
        pointer.x > this.l[i].x1 - 5 &&
        pointer.x < this.l[i].x1 + 5
      ) {
        if (this.isBeingSelected) {
          this.l[i].set({ stroke: 'green' });
          this.labels[i].set({ fill: 'green' });
          this.selectedIndex = i;
        }
        this.isBeingSelected = true;
        this.text.visible = true;
        this.text.set({ backgroundColor: this.backgroundColor });
        return true;
      }
    }
    this.isBeingSelected = false;
    this.selectedIndex = null;
    this.text.visible = false;
    this.text.set({ backgroundColor: '' });
    return false;
  };

  BeatComplexCalipersImpl.prototype.select = function () {
    this.l.forEach((line: fabric.Line, i: number) => {
      if (line.stroke !== 'green') {
        line.set({ stroke: 'blue' });
        this.labels[i].set({ fill: 'blue' });
      }
    });
  };

  BeatComplexCalipersImpl.prototype.deselect = function () {
    this.l.forEach((line: fabric.Line, i: number) => {
      line.set({ stroke: 'indigo' });
      this.labels[i].set({ fill: 'indigo' });
    });

    this.text.visible = false;
    this.text.set({ backgroundColor: '' });

    this.isBeingSelected = false;
  };

  BeatComplexCalipersImpl.prototype.isVisibility = function (isVisibility: boolean) {
    this.isVisible = isVisibility;
    this.l.forEach((line: fabric.Line, i: number) => {
      line.visible = isVisibility;
      this.labels[i].visible = isVisibility;
    });
    if (!isVisibility) {
      this.text.visible = isVisibility;
      this.text.set({ backgroundColor: '' });
    }
  };

  BeatComplexCalipersImpl.prototype.updateTextData = function () {
    const rate = getBpm(this.xr[2], this.xr[5], this.config.samplingRate).toFixed(0);
    const pr = getSeconds(this.xr[0], this.xr[1], this.config.samplingRate).toFixed(3);
    const qrs = getSeconds(this.xr[1], this.xr[3], this.config.samplingRate).toFixed(3);
    const qt = getSeconds(this.xr[1], this.xr[4], this.config.samplingRate).toFixed(3);
    const qtc = (Number(qt) / Math.sqrt(bpmToSec(Number(rate)))).toFixed(3);
    this.stringValue = ` Rate: ${rate} PR: ${pr} QRS: ${qrs} QT: ${qt} QTc: ${qtc} `;
    this.text.set({ text: this.stringValue });
  };

  BeatComplexCalipersImpl.prototype.adjustTextPos = function () {
    let pos = this.x[2] - this.text.width / 2;
    if (pos < 0) {
      pos = 0;
    } else if (pos + this.text.width > this.config.width) {
      pos = this.config.width - this.text.width;
    }
    this.text.set({
      left: pos,
    });
  };

  BeatComplexCalipersImpl.prototype.checkTextCondition = function () {
    if (this.isBeingSelected) {
      let count = 0;
      this.l.forEach((line: fabric.Line) => {
        if (!line.visible) {
          count += 1;
        }
      });
      this.text.visible = !(count === this.l.length);
      if (count === this.l.length) {
        this.text.set({ backgroundColor: '' });
      } else {
        this.text.set({ backgroundColor: this.backgroundColor });
      }
    }
  };

  BeatComplexCalipersImpl.prototype.setTextForExport = function () {
    let count = 0;
    this.l.forEach((line: fabric.Line) => {
      if (!line.visible) {
        count += 1;
      }
    });
    this.text.visible = !(count === this.l.length);
    if (count === this.l.length) {
      this.text.set({ backgroundColor: '' });
    } else {
      this.text.set({ backgroundColor: this.backgroundColor });
    }
  };

  BeatComplexCalipersImpl.prototype.getJSONObject = function () {
    const bccObj: Record<string, number> = {};
    this.xr.forEach((x: number, i: number) => {
      bccObj[BCC_LABELS[i]] = x;
    });
    return {
      type: this.type,
      channelIndex: this.channelIndex,
      xr: bccObj,
      text: this.stringValue,
    };
  };

  BeatComplexCalipersImpl.prototype.import = function (data: any) {
    this.stringValue = data.text;

    this.draw();

    const yPos = this.verticalChannelPlacement[this.channelIndex];
    const halfHeight = this.config.height / 9;

    for (let i = 0; i < this.x.length; i += 1) {
      this.l[i].set({
        y1: yPos - halfHeight,
        y2: yPos + halfHeight,
      });
      this.l[i].setCoords();
      this.labels[i].set({
        left: this.x[i] - this.labels[i].width / 2,
        top: yPos - halfHeight - 20,
      });
      this.labels[i].setCoords();

      // *: Set text position
      if (i === 2) {
        this.text.set({ text: this.stringValue });
        this.text.set({
          top: yPos - halfHeight * 0.75,
        });
        this.adjustTextPos();
        this.text.setCoords();
      }
      // *: Make them visible
      this.l[i].visible = true;
      this.labels[i].visible = true;
    }
  };
})();

const BeatComplexCalipers: BeatComplexCalipersCtor = BeatComplexCalipersImpl;

export { BeatComplexCalipers };
export default { BeatComplexCalipers };
